// Copyright 2023 Sorama B.V.

import { PlotData, PlotDataTypes } from "controls/plot/graph-plot/GraphPlot";
import { ArrayExtensions } from "extensions/ArrayExtensions";
import { Frequency } from "units/Frequency";
import { Generic } from "units/Generic";
import { CriticalBandUnit, Unit, UnitValue } from "units/Unit";

export interface FrequencyRange {
    from: number;
    to: number;
}

export interface MaxResult {
    frequency: number;
    value: number;
    unit?: Unit;
}

/**
 * Utility class for finding maximum values in spectral data within frequency ranges
 */
export class SpectralMaxFinder {
    /**
     * Find the frequency with the maximum value across all spectra within the given ranges
     * @param spectra Array of spectral data to search through
     * @param ranges Array of frequency ranges to search within (in Hz)
     * @returns The frequency (in Hz) with the maximum value, or null if no maximum found
     */
    public static findMaxFrequencyInRanges(spectra: PlotData[], ranges: FrequencyRange[]): number | null {
        const result = this.findMaxInRanges(spectra, ranges);
        if (!result) return null;

        return this.toHz(result.frequency, result.unit || Frequency.hertz);
    }

    /**
     * Find the frequency with the maximum value across all spectra within the given ranges
     * Returns a UnitValue for compatibility with existing SpectrumModule code
     * @param spectra Array of spectral data to search through
     * @param ranges Array of frequency ranges to search within (in Hz)
     * @returns UnitValue containing the frequency with the maximum value
     */
    public static findMaxFrequencyInRangesAsUnitValue(spectra: PlotData[], ranges: FrequencyRange[]): UnitValue {
        const result = this.findMaxInRanges(spectra, ranges);

        if (!result) {
            return new UnitValue(-1, Generic.unitless);
        }

        const frequencyInHz = this.toHz(result.frequency, result.unit || Frequency.hertz);
        if (frequencyInHz === null) {
            return new UnitValue(-1, Generic.unitless);
        }

        return new UnitValue(frequencyInHz, Frequency.hertz);
    }

    private static findMaxInRanges(spectra: PlotData[], ranges: FrequencyRange[]): MaxResult | null {
        if (!spectra || spectra.length === 0 || !ranges || ranges.length === 0) {
            return null;
        }

        let globalMaxFrequency = -1;
        let globalMaxValue = -Number.MAX_VALUE;
        let globalMaxUnit: Unit = Generic.unitless;

        for (const spectrum of spectra) {
            // Skip bar data as it's not relevant for frequency analysis
            if (spectrum.type === PlotDataTypes.BarData) continue;

            // Skip empty spectra
            if (!spectrum.data || spectrum.data.length === 0) continue;

            for (const range of ranges) {
                let from: number;
                let to: number;

                try {
                    // Convert Hz range to spectrum's unit for accurate comparison.
                    from = new UnitValue(range.from, Frequency.hertz).toUnit(spectrum.xUnit);
                    to = new UnitValue(range.to, Frequency.hertz).toUnit(spectrum.xUnit);
                } catch {
                    // Skip combinations where range/spectrum units cannot be converted.
                    continue;
                }

                const startIndex = ArrayExtensions.fastFindClosestValueIndex(spectrum.data, from, (point) => point.x);
                const stopIndex = ArrayExtensions.fastFindClosestValueIndex(spectrum.data, to, (point) => point.x);

                // Ensure we have valid indices
                if (startIndex === -1 || stopIndex === -1) continue;

                const minIndex = Math.min(startIndex, stopIndex);
                const maxIndex = Math.max(startIndex, stopIndex);

                for (let i = minIndex; i <= maxIndex; i++) {
                    const point = spectrum.data[i];
                    if (point.y > globalMaxValue) {
                        globalMaxValue = point.y;
                        globalMaxFrequency = point.x;
                        globalMaxUnit = spectrum.xUnit;
                    }
                }
            }
        }

        return globalMaxValue > -Number.MAX_VALUE
            ? { frequency: globalMaxFrequency, value: globalMaxValue, unit: globalMaxUnit }
            : null;
    }

    private static toHz(value: number, unit: Unit): number | null {
        if (unit instanceof CriticalBandUnit) {
            return unit.convertBandToHz(value);
        }

        try {
            // Covers Hz and logarithmic frequency units.
            return new UnitValue(value, unit).toUnit(Frequency.hertz);
        } catch {
            return null;
        }
    }
}
